import { BlacklistScope } from "@/enum/blacklist.enum";
import { CreateBlacklist } from "@/pages/Private/pages/Blacklist/schema/blacklist";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";

/** A lead's company as the blacklist needs it. */
export interface BlacklistableCompany {
	id: number;
	name?: string | null;
	primaryDomain?: string | null;
	linkedinUrl?: string | null;
}

/** A company as the toggle names it to the user. */
export interface NamedCompany {
	name?: string | null;
	domain?: string | null;
	linkedinCompanyUrl?: string | null;
}

/**
 * The companies a selection would block. `null` when they are not known up front, as for a
 * "select all" or while the server is still counting them.
 */
export type CompanySelection = {
	/** The companies to name; may be fewer than `count` when the server names only the first few. */
	companies: NamedCompany[];
	count: number;
	/** How many leads are selected, for the sentence that says none of them has a company. */
	leadCount: number;
} | null;

/** A translation key under `blacklist.companyToggle`, with what it interpolates. */
export interface CompanyToggleCopy {
	key: string;
	options?: Record<string, unknown>;
	disabled: boolean;
	/** The line under the toggle saying what blocking the company does; none when it cannot. */
	hintKey: "hint" | "hintSeveral" | null;
}

/**
 * The company entry for a lead's employer, as its own entry. It names the organization itself, so
 * it blocks the company whether or not we hold its website: built from the website alone, as it
 * used to be, it was never written for a company without one, and nobody was told (PREC-4425).
 *
 * Only the unsubscribe path writes it from the browser now. "Add to Blacklist" sends the selection
 * to `POST list/bulk-blacklist`, and the API's `companyBlacklistEntry` in
 * `list-person-blacklist-entries.util.ts` builds the same entry there (PREC-4435): change both.
 */
export function companyBlacklistEntry(
	company: BlacklistableCompany | null | undefined
): CreateBlacklist | null {
	if (!company?.id) {
		return null;
	}

	return {
		scope: BlacklistScope.ORGANIZATION,
		organizationId: company.id,
		domain: company.primaryDomain || undefined,
		linkedinCompanyUrl: company.linkedinUrl || undefined,
	};
}

/** The loaded rows with the given ids, in the order of the ids; an id with no row is skipped. */
export function leadsByIds(rows: ListPerson[], ids: number[]): ListPerson[] {
	const byId = new Map(rows.map((row) => [row.id, row]));

	return ids.map((id) => byId.get(id)).filter((row): row is ListPerson => !!row);
}

/** The companies of the given leads, each once, in the order the leads were given. */
export function companiesOfLeads(leads: ListPerson[]): NamedCompany[] {
	const seen = new Map<number, NamedCompany>();

	for (const lead of leads) {
		const organization = lead.prospect?.organization;

		if (organization?.id && !seen.has(organization.id)) {
			seen.set(organization.id, {
				name: organization.name,
				domain: organization.primaryDomain,
				linkedinCompanyUrl: organization.linkedinUrl,
			});
		}
	}

	return [...seen.values()];
}

/** The selection a set of leads describes, all of whose rows are at hand. */
export function selectionOfLeads(leads: ListPerson[]): CompanySelection {
	const companies = companiesOfLeads(leads);

	return { companies, count: companies.length, leadCount: leads.length };
}

const withoutScheme = (url: string): string =>
	url
		.trim()
		.replace(/^https?:\/\//i, "")
		.replace(/^www\./i, "")
		.replace(/\/+$/, "");

/** What a company is shown by beside its name: its website, else its LinkedIn page. */
export function companyIdentifier(company: NamedCompany): string | null {
	const identifier = company.domain || company.linkedinCompanyUrl;

	return identifier ? withoutScheme(identifier) || null : null;
}

/** What the "also blacklist the company" toggle says, and whether it can be switched on. */
export function companyToggleCopy(selection: CompanySelection): CompanyToggleCopy {
	if (!selection) {
		return { key: "unknown", disabled: false, hintKey: "hintSeveral" };
	}

	if (selection.count === 0) {
		return { key: "none", options: { count: selection.leadCount }, disabled: true, hintKey: null };
	}

	if (selection.count > 1) {
		return {
			key: "several",
			options: { count: selection.count },
			disabled: false,
			hintKey: "hintSeveral",
		};
	}

	return oneCompanyCopy(selection.companies[0]);
}

function oneCompanyCopy(company: NamedCompany | undefined): CompanyToggleCopy {
	const name = company?.name?.trim();
	const identifier = company ? companyIdentifier(company) : null;

	if (name && identifier) {
		return { key: "one", options: { name, identifier }, disabled: false, hintKey: "hint" };
	}

	if (name || identifier) {
		return {
			key: "oneByName",
			options: { name: name || identifier },
			disabled: false,
			hintKey: "hint",
		};
	}

	return { key: "oneUnnamed", disabled: false, hintKey: "hint" };
}
